import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useProdAuth } from '../../contexts/ProdAuthContext';

const ACCENT = '#4A7C59';

interface Delivery {
  id: string;
  deliveryDate: string;
  cherryKg: string;
  cherryQuality: string;
  pricePerKg: string | null;
  totalAmount: string | null;
  notes: string;
  farmerMemberId: string | null;
  farmerName: string | null;
  farmerWard: string | null;
  tripId: string | null;
  tripDate: string | null;
  tripCentreName: string | null;
  vehicleReg: string | null;
  createdAt: string;
}

interface DailySummary {
  deliveryCount: number;
  totalKg: string;
  totalAmount: string;
}

const QUALITY_BADGE: Record<string, string> = {
  A: 'bg-green-100 text-green-700',
  B: 'bg-yellow-100 text-yellow-700',
  C: 'bg-red-100 text-red-700',
};

export const DeliveryList: React.FC = () => {
  const { user } = useProdAuth();
  const isHub = user && ['admin', 'super_admin'].includes(user.role);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<DailySummary | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');

  const PAGE_SIZE = 50;

  // Today's summary
  useEffect(() => {
    fetch('/api/production/deliveries?summary=today', { credentials: 'include' })
      .then(r => r.json())
      .then(setSummary)
      .catch(() => {});
  }, []);

  const fetchDeliveries = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
    if (appliedFrom) params.set('from', appliedFrom);
    if (appliedTo) params.set('to', appliedTo);

    try {
      const res = await fetch(`/api/production/deliveries?${params}`, { credentials: 'include' });
      const data = await res.json();
      setDeliveries(data.deliveries || []);
      setTotal(data.total || 0);
    } catch {
      setDeliveries([]);
    } finally {
      setLoading(false);
    }
  }, [page, appliedFrom, appliedTo]);

  useEffect(() => { fetchDeliveries(); }, [fetchDeliveries]);

  const applyFilter = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
    setPage(1);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#1A1A1A]">Cherry deliveries</h2>
          <p className="text-sm text-[#6B6B6B] mt-0.5">{total.toLocaleString()} total records</p>
        </div>
        <Link
          to="/production/deliveries/new"
          className="px-4 py-2.5 text-sm font-medium text-white rounded-lg"
          style={{ backgroundColor: ACCENT }}
        >
          + Record delivery
        </Link>
      </div>

      {/* Today's summary */}
      {summary && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: "Today's deliveries", value: summary.deliveryCount },
            { label: 'Total cherry kg', value: `${parseFloat(summary.totalKg || '0').toLocaleString()} kg` },
            { label: 'Total value (KES)', value: parseFloat(summary.totalAmount || '0') > 0 ? parseFloat(summary.totalAmount).toLocaleString() : '—' },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white border border-[#E8E6E2] rounded-xl p-4">
              <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-1">{label}</p>
              <p className="text-2xl font-bold text-[#1A1A1A]">{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Date filter */}
      <form onSubmit={applyFilter} className="flex items-end gap-3 mb-6">
        <div>
          <label className="block text-xs text-[#6B6B6B] mb-1">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="px-3 py-2 border border-[#E8E6E2] rounded-lg text-sm bg-white focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs text-[#6B6B6B] mb-1">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="px-3 py-2 border border-[#E8E6E2] rounded-lg text-sm bg-white focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white rounded-lg"
          style={{ backgroundColor: ACCENT }}
        >
          Filter
        </button>
        {(appliedFrom || appliedTo) && (
          <button
            type="button"
            onClick={() => { setDateFrom(''); setDateTo(''); setAppliedFrom(''); setAppliedTo(''); setPage(1); }}
            className="px-4 py-2 text-sm text-[#6B6B6B] border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8]"
          >
            Clear
          </button>
        )}
      </form>

      {/* Table */}
      <div className="bg-white border border-[#E8E6E2] rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-sm text-[#6B6B6B]">Loading...</div>
        ) : deliveries.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-sm text-[#6B6B6B] mb-3">No deliveries recorded yet</p>
            <Link
              to="/production/deliveries/new"
              className="text-sm font-medium"
              style={{ color: ACCENT }}
            >
              Record the first delivery →
            </Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8E6E2] bg-[#FAFAF8]">
                <th className="text-left px-4 py-3 text-xs text-[#A3A3A3] uppercase tracking-wider font-medium">Date</th>
                <th className="text-left px-4 py-3 text-xs text-[#A3A3A3] uppercase tracking-wider font-medium">{isHub ? 'From station' : 'Farmer'}</th>
                <th className="text-right px-4 py-3 text-xs text-[#A3A3A3] uppercase tracking-wider font-medium">Kg</th>
                <th className="text-left px-4 py-3 text-xs text-[#A3A3A3] uppercase tracking-wider font-medium">Grade</th>
                <th className="text-right px-4 py-3 text-xs text-[#A3A3A3] uppercase tracking-wider font-medium">Amount (KES)</th>
                <th className="text-left px-4 py-3 text-xs text-[#A3A3A3] uppercase tracking-wider font-medium">Notes</th>
              </tr>
            </thead>
            <tbody>
              {deliveries.map((d, i) => (
                <tr key={d.id} className={`border-b border-[#E8E6E2] last:border-0 ${i % 2 === 0 ? '' : 'bg-[#FAFAF8]'}`}>
                  <td className="px-4 py-3 text-[#1A1A1A] font-mono text-xs">
                    {new Date(d.deliveryDate).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-4 py-3">
                    {d.tripId ? (
                      <div>
                        <span className="font-medium text-[#1A1A1A]">{d.tripCentreName || 'Hub trip'}</span>
                        {d.vehicleReg && <span className="text-[#6B6B6B] ml-1.5 text-xs font-mono">{d.vehicleReg}</span>}
                      </div>
                    ) : d.farmerMemberId ? (
                      <div>
                        <span className="font-medium text-[#1A1A1A]">{d.farmerMemberId}</span>
                        {d.farmerName && <span className="text-[#6B6B6B] ml-1.5">{d.farmerName}</span>}
                        {d.farmerWard && <p className="text-xs text-[#A3A3A3]">{d.farmerWard}</p>}
                      </div>
                    ) : (
                      <span className="text-[#A3A3A3]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-[#1A1A1A]">
                    {parseFloat(d.cherryKg).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${QUALITY_BADGE[d.cherryQuality] || 'bg-neutral-100 text-neutral-500'}`}>
                      {d.cherryQuality}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-[#6B6B6B]">
                    {d.totalAmount ? parseFloat(d.totalAmount).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-[#A3A3A3] text-xs max-w-xs truncate">{d.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-[#6B6B6B]">
          <span>Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 border border-[#E8E6E2] rounded-lg disabled:opacity-40 hover:bg-[#F0EDE8]"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 border border-[#E8E6E2] rounded-lg disabled:opacity-40 hover:bg-[#F0EDE8]"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
